/**
 * Chọn nội dung theo ngôn ngữ. Nguồn gốc viết bằng tiếng Việt; bản tiếng Anh nằm ở src/data/en/*
 * (khóa theo id) và được test bảo đảm phủ đủ. Đề thi (prompt/options) luôn tiếng Anh — xem questions/index.ts.
 */
import type { Lang } from "../i18n/lang";
import { EN_FLASHCARDS } from "./en/flashcards";
import { EN_GLOSSARY } from "./en/glossary";
import { EN_LABS } from "./en/labs";
import { EN_LESSONS, EN_STAGES } from "./en/lessons";
import { FLASHCARDS, type Flashcard } from "./flashcards";
import { GLOSSARY, type GlossaryTerm } from "./glossary";
import type { Lab } from "./labTypes";
import { LESSONS, STAGES, type Lesson, type Stage } from "./lessons";
import type { Question } from "./types";

export function localizeLesson(l: Lesson, lang: Lang): Lesson {
  const en = lang === "en" ? EN_LESSONS[l.id] : undefined;
  return en ? { ...l, ...en } : l;
}

export function localizeStage(s: Stage, lang: Lang): Stage {
  const en = lang === "en" ? EN_STAGES[s.id] : undefined;
  return en ? { ...s, ...en } : s;
}

const memo = <T,>(f: (lang: Lang) => T) => {
  const cache = new Map<Lang, T>();
  return (lang: Lang): T => {
    let v = cache.get(lang);
    if (v === undefined) { v = f(lang); cache.set(lang, v); }
    return v;
  };
};

export const lessonsFor = memo((lang) => LESSONS.map((l) => localizeLesson(l, lang)));
export const stagesFor = memo((lang) => STAGES.map((s) => localizeStage(s, lang)));

export function localizeLab(lab: Lab, lang: Lang): Lab {
  const en = lang === "en" ? EN_LABS[lab.id] : undefined;
  if (!en) return lab;
  const text = { title: en.title, skill: en.skill, intro: en.intro, task: en.task, takeaway: en.takeaway };
  switch (lab.type) {
    case "order":
      return { ...lab, ...text, steps: en.steps ?? lab.steps };
    case "classify":
      return {
        ...lab,
        ...text,
        buckets: en.buckets ?? lab.buckets,
        items: lab.items.map((it, i) => ({ ...it, text: en.items?.[i]?.text ?? it.text, why: en.items?.[i]?.why ?? it.why })),
      };
    case "spot":
      return { ...lab, ...text, lines: en.lines ?? lab.lines, fix: en.fix ?? lab.fix };
    case "build":
      return {
        ...lab,
        ...text,
        starter: en.starter ?? lab.starter,
        solution: en.solution ?? lab.solution,
        checks: lab.checks.map((c, i) => ({ ...c, label: en.checks?.[i]?.label ?? c.label, hint: en.checks?.[i]?.hint ?? c.hint })),
      };
  }
}

export function localizeCard(c: Flashcard, lang: Lang): Flashcard {
  const en = lang === "en" ? EN_FLASHCARDS[c.id] : undefined;
  return en ? { ...c, ...en } : c;
}
export const flashcardsFor = memo((lang) => FLASHCARDS.map((c) => localizeCard(c, lang)));

export function localizeTerm(t: GlossaryTerm, lang: Lang): GlossaryTerm {
  const en = lang === "en" ? EN_GLOSSARY[t.term] : undefined;
  return en ? { ...t, def: en } : t;
}
export const glossaryFor = memo((lang) => GLOSSARY.map((t) => localizeTerm(t, lang)));

/** Giải thích theo ngôn ngữ; đề bài/đáp án KHÔNG đổi (luôn tiếng Anh như đề thật). */
export function explanationFor(q: Question, lang: Lang): string {
  return lang === "en" && q.explanationEn ? q.explanationEn : q.explanation;
}
